import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  X, ChevronRight, ChevronLeft, Check, Loader2,
  ShoppingCart, BarChart2, FileDown, Target, Warehouse, Factory,
  Rocket, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Config ──────────────────────────────────────────────────────────────────
const OPTIONAL_MODULES = [
  { key: 'orders',     label: 'Ordini & DDT',      desc: 'Ordini clienti, DDT e documenti di trasporto', icon: ShoppingCart, group: 'Vendite'   },
  { key: 'analytics',  label: 'Analisi Ordini',     desc: 'Dashboard analytics: trend, top clienti/prodotti', icon: BarChart2, group: 'Analisi'   },
  { key: 'reports',    label: 'Report',             desc: 'Esporta report PDF ed Excel su vendite e forecast', icon: FileDown,  group: 'Analisi'   },
  { key: 'forecast',   label: 'Forecast & Budget',  desc: 'Budget commerciale mensile/settimanale per prodotto', icon: Target, group: 'Analisi'   },
  { key: 'inventory',  label: 'Scorte',             desc: 'Materie prime, movimenti e piano acquisti',    icon: Warehouse,   group: 'Operativo' },
  { key: 'production', label: 'Produzione',         desc: 'Distinta base, piani produzione e MRP',        icon: Factory,     group: 'Operativo' },
];

const ROLES = [
  { key: 'sales_manager', label: 'Resp. Vendite',  color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'       },
  { key: 'sales_rep',     label: 'Commerciale',    color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300' },
  { key: 'operator',      label: 'Operatore',      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'   },
  { key: 'client',        label: 'Cliente',        color: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300'   },
];

const PRESETS = {
  none:     { label: 'Nessuno',  cls: 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400',           perm: { view: false, create: false, edit: false, delete: false } },
  readonly: { label: 'Lettura',  cls: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-400',      perm: { view: true,  create: false, edit: false, delete: false } },
  standard: { label: 'Standard', cls: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', perm: { view: true, create: true, edit: true, delete: false } },
  full:     { label: 'Completo', cls: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/20 dark:text-violet-400', perm: { view: true, create: true, edit: true, delete: true  } },
};

const detectPreset = (p) => {
  if (!p?.view) return 'none';
  if (!p?.create) return 'readonly';
  if (!p?.delete) return 'standard';
  return 'full';
};

const GROUPS = ['Vendite', 'Analisi', 'Operativo'];

const STEP_LABELS = ['Benvenuto', 'Moduli', 'Permessi', 'Pronto!'];

// ── StepIndicator ────────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300
              ${i < step  ? 'bg-emerald-500 text-white'
              : i === step ? 'bg-primary text-primary-foreground shadow-md ring-4 ring-primary/20'
              : 'bg-muted text-muted-foreground'}`}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <span className={`text-[10px] font-medium whitespace-nowrap hidden sm:block
              ${i === step ? 'text-primary' : 'text-muted-foreground'}`}>{label}</span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div className={`h-0.5 w-12 sm:w-16 mb-4 transition-all duration-500 ${i < step ? 'bg-emerald-500' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── PresetSelector ────────────────────────────────────────────────────────────
function PresetSelector({ value, onChange, disabled }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {Object.entries(PRESETS).map(([key, p]) => (
        <button
          key={key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(key)}
          className={`text-[11px] font-semibold px-2 py-1 rounded border transition-all
            ${value === key ? p.cls + ' ring-1 ring-offset-1 ring-current' : 'border-border bg-background text-muted-foreground hover:border-primary/50'}
            ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── SetupWizard ───────────────────────────────────────────────────────────────
export default function SetupWizard({ tenantName, onClose }) {
  const [step, setStep] = useState(0);
  const [modules, setModules] = useState({});              // { key: bool }
  const [wizardPerms, setWizardPerms] = useState({});      // { role: { module: presetKey } }
  const [existingPerms, setExistingPerms] = useState({});  // full perms from API
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load current config
  useEffect(() => {
    const load = async () => {
      try {
        const [modsRes, permsRes] = await Promise.all([
          axios.get(`${API}/admin/my-modules`),
          axios.get(`${API}/admin/permissions`),
        ]);
        const optMods = {};
        OPTIONAL_MODULES.forEach(m => { optMods[m.key] = modsRes.data[m.key] ?? false; });
        setModules(optMods);

        const stored = permsRes.data.roles || {};
        setExistingPerms(stored);
        const wp = {};
        ROLES.forEach(r => {
          wp[r.key] = {};
          OPTIONAL_MODULES.forEach(m => {
            wp[r.key][m.key] = detectPreset(stored[r.key]?.[m.key]);
          });
        });
        setWizardPerms(wp);
      } catch {
        toast.error('Errore nel caricamento configurazione');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleModule = (key) => setModules(prev => ({ ...prev, [key]: !prev[key] }));
  const setPreset = (role, module, preset) =>
    setWizardPerms(prev => ({ ...prev, [role]: { ...prev[role], [module]: preset } }));

  const enabledModules = OPTIONAL_MODULES.filter(m => modules[m.key]);

  const buildPermsPayload = useCallback(() => {
    const result = {};
    ROLES.forEach(r => {
      result[r.key] = { ...(existingPerms[r.key] || {}) };
      OPTIONAL_MODULES.forEach(m => {
        const presetKey = wizardPerms[r.key]?.[m.key] || 'none';
        result[r.key][m.key] = modules[m.key] ? PRESETS[presetKey].perm : PRESETS.none.perm;
      });
    });
    return { roles: result };
  }, [existingPerms, wizardPerms, modules]);

  const handleComplete = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/admin/my-modules`, modules),
        axios.put(`${API}/admin/permissions`, buildPermsPayload()),
      ]);
      await axios.post(`${API}/admin/complete-setup`);
      setStep(3);
    } catch {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem('wizard_dismissed', '1');
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-background rounded-2xl shadow-2xl w-full transition-all duration-300
        ${step === 2 ? 'max-w-3xl' : 'max-w-2xl'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Configurazione iniziale
          </span>
          {step < 3 && (
            <button onClick={handleSkip} className="text-muted-foreground hover:text-foreground transition-colors" title="Salta per ora">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="px-6 pb-6">
          <StepIndicator step={step} />

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" size={28} /></div>
          ) : (
            <>
              {/* ── Step 0: Welcome ────────────────────────────────────────── */}
              {step === 0 && (
                <div className="text-center space-y-4 py-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
                    <Rocket size={38} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Benvenuto in AGRI-CRM
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      Configura <strong>{tenantName}</strong> in 2 minuti
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm mt-4">
                    {[
                      { step: '1', label: 'Scegli i moduli', desc: 'Attiva solo ciò che ti serve' },
                      { step: '2', label: 'Imposta i permessi', desc: 'Chi può fare cosa' },
                      { step: '3', label: 'Pronto!', desc: 'Inizia a usare il CRM' },
                    ].map(s => (
                      <div key={s.step} className="bg-muted/40 rounded-xl p-3 text-center border border-border">
                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mx-auto mb-2">{s.step}</div>
                        <p className="font-semibold text-xs">{s.label}</p>
                        <p className="text-muted-foreground text-[11px] mt-0.5">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Step 1: Modules ────────────────────────────────────────── */}
              {step === 1 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Quali moduli vuoi attivare?
                  </h2>
                  <p className="text-sm text-muted-foreground mb-5">
                    I moduli core (Aziende, Contatti, Pipeline, Calendario, Attività) sono sempre attivi.
                  </p>
                  <div className="space-y-5">
                    {GROUPS.map(group => {
                      const groupMods = OPTIONAL_MODULES.filter(m => m.group === group);
                      return (
                        <div key={group}>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{group}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {groupMods.map(m => {
                              const Icon = m.icon;
                              const on = modules[m.key];
                              return (
                                <div
                                  key={m.key}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleModule(m.key)}
                                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleModule(m.key)}
                                  data-testid={`wizard-module-${m.key}`}
                                  className={`flex items-start gap-3 p-3.5 rounded-xl border text-left w-full cursor-pointer transition-all
                                    ${on
                                      ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-600 shadow-sm'
                                      : 'border-border bg-background hover:bg-muted/40'
                                    }`}
                                >
                                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                                    ${on ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                                    <Icon size={16} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className={`text-sm font-semibold ${on ? 'text-emerald-800 dark:text-emerald-300' : ''}`}>{m.label}</p>
                                      <Switch checked={on} onCheckedChange={() => toggleModule(m.key)}
                                        className="flex-shrink-0" onClick={e => e.stopPropagation()} />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{m.desc}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Step 2: Permissions ────────────────────────────────────── */}
              {step === 2 && (
                <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                  <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Chi può fare cosa?
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configura i permessi per ogni ruolo sui moduli attivati.
                  </p>
                  {enabledModules.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <p className="text-sm">Nessun modulo opzionale attivo.</p>
                      <p className="text-xs mt-1">Torna al passo precedente per attivare almeno un modulo.</p>
                    </div>
                  ) : (
                    <Tabs defaultValue={ROLES[0].key}>
                      <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
                        {ROLES.map(r => (
                          <TabsTrigger key={r.key} value={r.key} className="text-xs" data-testid={`wizard-perm-tab-${r.key}`}>
                            {r.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {ROLES.map(r => (
                        <TabsContent key={r.key} value={r.key} className="mt-0">
                          <div className="space-y-2">
                            {enabledModules.map(m => {
                              const Icon = m.icon;
                              const current = wizardPerms[r.key]?.[m.key] || 'none';
                              return (
                                <div key={m.key}
                                  className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-muted/20"
                                  data-testid={`wizard-perm-${r.key}-${m.key}`}>
                                  <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                                    <Icon size={13} className="text-muted-foreground" />
                                  </div>
                                  <span className="text-sm font-medium w-36 flex-shrink-0">{m.label}</span>
                                  <PresetSelector value={current} onChange={(v) => setPreset(r.key, m.key, v)} />
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-3 flex gap-4 flex-wrap">
                            {Object.entries(PRESETS).map(([k, p]) => (
                              <span key={k}><strong className="font-semibold">{p.label}</strong>: {
                                k === 'none' ? 'nessun accesso' :
                                k === 'readonly' ? 'solo lettura' :
                                k === 'standard' ? 'vedi + crea + modifica' :
                                'accesso completo'
                              }</span>
                            ))}
                          </p>
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}
                </div>
              )}

              {/* ── Step 3: Done ───────────────────────────────────────────── */}
              {step === 3 && (
                <div className="text-center space-y-4 py-6 animate-in fade-in zoom-in-95 duration-300">
                  <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
                    <Check size={40} className="text-white stroke-[3]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Configurazione completata!
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      <strong className="text-foreground">{enabledModules.length}</strong> moduli attivi ·{' '}
                      <strong className="text-foreground">{ROLES.length}</strong> ruoli configurati
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 mt-2">
                    {enabledModules.map(m => {
                      const Icon = m.icon;
                      return (
                        <span key={m.key} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          <Icon size={12} />{m.label}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Puoi modificare queste impostazioni in qualsiasi momento da <strong>Impostazioni → Moduli</strong> e <strong>Gestione Ruoli</strong>.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── Footer buttons ─────────────────────────────────────────────── */}
          {!loading && (
            <div className={`flex items-center mt-6 ${step === 0 ? 'justify-center flex-col gap-2 sm:flex-row sm:justify-center' : 'justify-between'}`}>
              {step > 0 && step < 3 && (
                <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} className="gap-1">
                  <ChevronLeft size={15} /> Indietro
                </Button>
              )}
              {step === 0 && (
                <>
                  <Button onClick={() => setStep(1)} size="lg" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                    Inizia configurazione <ArrowRight size={16} />
                  </Button>
                  <button onClick={handleSkip} className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors">
                    Salta per ora
                  </button>
                </>
              )}
              {step === 1 && (
                <Button onClick={() => setStep(2)} className="gap-1 ml-auto">
                  Continua <ChevronRight size={15} />
                </Button>
              )}
              {step === 2 && (
                <Button onClick={handleComplete} disabled={saving} className="gap-1 ml-auto bg-emerald-600 hover:bg-emerald-700 text-white">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                  Completa configurazione
                </Button>
              )}
              {step === 3 && (
                <Button onClick={onClose} className="mx-auto gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                  Vai alla Dashboard <ArrowRight size={16} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
